/* eslint-disable max-len */
import Vue from "vue";
import Router from "vue-router";
import i18n from "@/i18n";
import {
	IsAuthenticated,
	IsCompanyAdmin,
	IsGlobalAdmin,
	DefaultSettingsLoaded,
	CanAcessMonitoringElement,
} from "@/services/common/auth/RouterService";
import store from "@/stores/store.js";
import { SET_REDIRECT_ROUTE, RESET_REDIRECT_ROUTE } from "@/stores/router/RouterActionTypes";

Vue.use(Router);

// #region  Guard Methods
const isSae = process.env.VUE_APP_IS_SAE === "true";

const IfIsNotAuthenticated = (to, from, next) =>
{
	if (!IsAuthenticated())
	{
		return next();
	}

	return next(`/${i18n.locale}/`);
};

const IfIsAuthenticated = async(to, from, next) =>
{
	const isSettingsLoaded = await DefaultSettingsLoaded();

	if (!isSettingsLoaded)
	{
		store.dispatch(`RouterModule/${SET_REDIRECT_ROUTE}`, to);
		return next(`/${i18n.locale}/login`);
	}

	const redirectAfterLogin = store.state.RouterModule.redirectRoute;

	if (redirectAfterLogin)
	{
		store.dispatch(`RouterModule/${RESET_REDIRECT_ROUTE}`);

		const redirectPath = redirectAfterLogin.fullPath;
		if (redirectPath !== to.fullPath)
		{
			return next(redirectPath);
		}
	}

	return next();
};

const IfHasAdminPermission = async(to, from, next) =>
{
	if (await DefaultSettingsLoaded() && (IsGlobalAdmin() || IsCompanyAdmin()))
	{
		return next();
	}
	return next(`/${i18n.locale}/`);
};

const IfHasGlobalAdminPermission = async(to, from, next) =>
{
	if (await DefaultSettingsLoaded() && IsGlobalAdmin())
	{
		return next();
	}
	return next(`/${i18n.locale}/`);
};

const IsMonitoringElementFromSameUserCompany = async(to, from, next) =>
{
	if (await DefaultSettingsLoaded())
	{
		const canAccessMonitoringElements =
			CanAcessMonitoringElement(to.params.parentMonitoringElementId) ||
			CanAcessMonitoringElement(to.params.monitoringElementId);

		if (canAccessMonitoringElements || IsGlobalAdmin())
		{
			return next();
		}
	}
	return next(`/${i18n.locale}/`);
};

// #endregion

export default new Router({
	mode:   "history",
	base:   process.env.BASE_URL,
	routes: [
		{
			path:      "/:lang",
			component: {
				render(c)
				{
					return c("router-view");
				},
			},
			children: [
				{
					path:     "/",
					redirect: { path: "/:lang/home" },
				},
				{
					path:      "home",
					name:      "Home",
					component: () =>
					{
						return import("@/pages/wind/WindHome");
					},
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:  "home/trend",
					props: (route) => ({
						query: {
							assets:           route.query.assets,
							turbineId:        route.query.turbineId,
							notificationDate: route.query.notificationDate,
							trendSignalId:    route.query.trendSignalId,
							trendFrequencyId: route.query.trendFrequencyId,
						}
					}),
					name:        "WindHomeTrends",
					component:   () => import("@/pages/common/trend/Trend.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "login",
					name:        "Login",
					component:   () => import("@/pages/common/Login.vue"),
					beforeEnter: IfIsNotAuthenticated,
				},
				{
					path:        "profile",
					name:        "Profile",
					component:   () => import("@/pages/common/Profile.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings",
					name:        "Configurations",
					component:   () => import("@/pages/common/configurations/Configurations.vue"),
					beforeEnter: IfHasAdminPermission,
				},
				{
					path:        "settings/users",
					name:        "UserConfigurations",
					component:   () => import("@/pages/common/configurations/users/UsersConfigurations.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings/companyTrendLimits",
					name:        "CompanyTrendLimitsConfiguration",
					component:   () => import("@/pages/common/configurations/company-limits-and-notification/CompanyTrendLimitConfiguration.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings/frequencyTrendLimitConfiguration",
					name:        "FrequencyTrendLimitConfiguration",
					component:   () => import("@/pages/common/configurations/frequency-trend-limit-configuration/FrequencyTrendLimitConfiguration.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "alarm-management",
					name:        "AlarmManagement",
					component:   () => import("@/pages/common/alarm-management/AlarmsPage.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings/limitsCreator",
					name:        "FrequencyTrendLimitCreator",
					component:   () => import("@/pages/common/configurations/frequency-trend-limit-configuration/LimitsCreator.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings/kinematic-configuration",
					name:        "KinematicConfiguration",
					component:   () => import("@/pages/wind/configurations/kinematic/KinematicParametersPage.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "settings/frequencyTrendConfiguration",
					name:        "FrequencyTrendConfiguration",
					component:   () => import("@/pages/common/configurations/frequency-component/FrequencyTrendConfiguration.vue"),
					beforeEnter: IfIsAuthenticated,
				},
				{
					path:        "wind/park",
					name:        "WindParkStage",
					redirect:    { path: "/:lang/home" },
					component:   () => import("@/pages/wind/park/FarmUnit.vue"),
					beforeEnter: IsMonitoringElementFromSameUserCompany,
					children:    [
						{
							path:        ":parentMonitoringElementId/turbines",
							name:        "WindPark",
							component:   () => import("@/pages/wind/park/WindPark.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":parentMonitoringElementId/turbines/trend",
							props:       (route) => ({ query: route.query.park_asset }),
							name:        "WindParkTrends",
							component:   () => import("@/pages/common/trend/Trend.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
					],
				},
				{
					path:        "wind/park/:parentMonitoringElementId/turbines",
					name:        "WindGeneratingUnit",
					redirect:    { path: "/:lang/home" },
					component:   () => import("@/pages/wind/generating-unit/GeneratingUnit.vue"),
					beforeEnter: IsMonitoringElementFromSameUserCompany,
					children:    [
						{
							path:        ":monitoringElementId",
							name:        "WindTurbine",
							component:   () => import("@/pages/wind/generating-unit/wind-turbine/WindTurbine.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/diagnostic/configurator",
							name:        "WindDiagnosticSettings",
							component:   () => import("@/pages/wind/WindDiagnosticSettings.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/trend",
							name:        "WindTrends",
							component:   () => import("@/pages/common/trend/Trend.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/registers",
							name:        "WindRegisters",
							component:   () => import("@/pages/common/Registers.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/audio",
							name:        "WindAudio",
							component:   () => import("@/pages/common/Audio.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:  ":monitoringElementId/vdi",
							name:  "VDIWind",
							props: (route) => ({
								query: {
									component:   route.query.component,
									worstSignal: route.query.worstSignal,
									lastVDIDate: route.query.lastVDIDate,
								},
							}),
							component:   () => import("@/pages/wind/WindVDI.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/pitch-analysis",
							name:        "PitchAnalysis",
							component:   () => import("@/pages/wind/pitch-analysis/PitchAnalysis.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:  ":monitoringElementId/oil",
							name:  "WindOil",
							props: (route) => ({
								query: {
									powerId:    route.query.powerId,
									protocolId: route.query.protocolId,
									ratedPower: route.query.ratedPower,
								},
							}),
							component:   () => import("@/pages/wind/WindOil.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/notifications",
							name:        "WindLimitsSettings",
							component:   () => import("@/pages/common/configurations/limits/LimitsSettings"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
					],
				},
				{
					path:        "hydro/plant/:parentMonitoringElementId/generating_units",
					name:        "HydroElectricGeneratingUnit",
					redirect:    { path: "/:lang/home" },
					component:   () => import("@/pages/hydro/generating-unit/GeneratingUnit.vue"),
					beforeEnter: IsMonitoringElementFromSameUserCompany,
					children:    [
						{
							path:  ":monitoringElementId/anomalies",
							name:  "AnomalyDetection",
							props: (route) => ({
								query: {
									name: route.query.name,
									id:   route.query.id,
								},
							}),
							component:   () => import("@/pages/common/Anomaly.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:  ":monitoringElementId/vdi",
							name:  "VDIHydro",
							props: (route) => ({
								query: {
									component:   route.query.component,
									worstSignal: route.query.worstSignal,
									lastVDIDate: route.query.lastVDIDate,
								},
							}),
							component:   () => import("@/pages/hydro/HydroVDI.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/blade_defect/:resultId",
							name:        "BladeDefect",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/blade-defect/BladeDefect.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/pressure_fluctuation/:resultId",
							name:        "PressureFluctuation",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/pressure-fluctuation/PressureFluctuation.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/oil_whirl/:resultId",
							name:        "OilWhirl",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/oil-whirl/OilWhirl.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/angular_misalignment/:resultId",
							name:        "AngularMisalignment",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/angular-misalignment/AngularMisalignment.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/parallel_misalignment/:resultId",
							name:        "ParallelMisalignment",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/parallel-misalignment/ParallelMisalignment.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/unbalance/:resultId",
							name:        "Unbalance",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/unbalance/Unbalance.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/looseness/:resultId",
							name:        "Looseness",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/looseness/Looseness.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/bearing_pads_defect/:resultId",
							name:        "BearingPadsDefect",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/bearing-pads-defect/BearingPadsDefect.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/stator_defect/:resultId",
							name:        "StatorDefect",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/stator-defect/StatorDefect.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/overhung_rotor/:resultId",
							name:        "OverhungRotor",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/overhung-rotor/OverhungRotor.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/rubbing/:resultId",
							name:        "Rubbing",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/rubbing/Rubbing.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/magnetic_flux/:resultId",
							name:        "MagneticFlux",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/magnetic-flux/MagneticFlux.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/airgap/:resultId",
							name:        "AirGap",
							component:   () => import("@/pages/hydro/diagnostics/failure-modes/air-gap/AirGap.vue"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
						{
							path:        ":monitoringElementId/notifications",
							name:        "HydroLimitsSettings",
							component:   () => import("@/pages/common/configurations/limits/LimitsSettings"),
							beforeEnter: IsMonitoringElementFromSameUserCompany,
						},
					],
				},
			],
		},
	],
});
